import React, {FC, useContext} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {HotelCardContext} from 'components/HotelCardContext'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {getUserMeasurementSystem} from 'modules/meta/selectors'
import {getGeoDetails, getPlaceCategory} from 'modules/sapiSearch/selectors'
import {getIsUserOrUrlLocationSearch} from 'modules/search/selectors'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {
  convertMilesToKm,
  getDistanceFromMessage,
  messages
} from '@vio/core/src/utils/distanceFrom'

const MessageWrapper = styled.div`
  ${cssTheme.typography.bodyXS};
  overflow: hidden;
  word-wrap: break-word;
`

interface Props {
  distance?: number
}

export const DistanceFrom: FC<Props> = ({distance}) => {
  const {formatMessage} = useIntl()
  const {hotelId} = useContext(HotelCardContext)
  const userMeasurementSystem = useSelector(getUserMeasurementSystem)
  const destination = useDestinationDisplayName()
  const placeCategory = useSelector(getPlaceCategory)
  const {featureType: geoDetailsFeatureType, shortName: geoDetailsShortName} =
    useSelector(getGeoDetails) || {}
  const fromUserLocation = useSelector(getIsUserOrUrlLocationSearch)

  const distanceFromMessage = getDistanceFromMessage({
    fromUserLocation,
    geoDetailsFeatureType,
    geoDetailsShortName,
    placeCategory: placeCategory || 0
  })

  // Show nothing when distance or message not provided
  if (!distance || !distanceFromMessage) return null

  const [unitMessage, convertedDistance] =
    userMeasurementSystem === 'imperial'
      ? [messages.distanceAsMiles, distance]
      : [messages.distanceAsKm, convertMilesToKm(distance)]

  const message = formatMessage(distanceFromMessage, {
    distance: convertedDistance.toFixed(1),
    unit: formatMessage(unitMessage),
    targetName: geoDetailsShortName ?? destination
  })

  return (
    <Box
      display="flex"
      color="content.neutral.secondary"
      alignItems="flex-start"
    >
      <Icon name="Walk" size="sm" />
      <MessageWrapper id={`HotelCardMobileDistanceFrom-${hotelId}`}>
        {message}
      </MessageWrapper>
    </Box>
  )
}
